import { useMemo, type PointerEventHandler, type RefObject } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import type {
  ProductGroupSegment,
  ProductGroupsChartData,
} from './report-product-groups.model';
import {
  formatProductGroupSegmentTooltip,
  getProductGroupsDonutCenterLabel,
} from './report-product-groups.helpers';
import {
  buildProductGroupDonutSliceGeometries,
  buildVariableOuterDonutPath,
  PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB,
} from './product-groups-donut.helpers';
import {
  PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT,
  PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT,
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT,
  PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR,
  PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB,
} from './report-product-groups.constants';
import {
  PRODUCT_GROUPS_CENTER_TEXT,
  PRODUCT_GROUPS_TEXT_COLOR,
} from './report-product-groups.typography';

type ProductGroupsDonutChartProps = {
  data: ProductGroupsChartData;
  rotationOffsetDeg?: number;
  isDragging?: boolean;
  slotRef?: RefObject<HTMLDivElement | null>;
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  onPointerMove?: PointerEventHandler<HTMLDivElement>;
  onPointerUp?: PointerEventHandler<HTMLDivElement>;
  onPointerCancel?: PointerEventHandler<HTMLDivElement>;
};

type BuiltSegment = ProductGroupSegment & {
  path: string;
  /** Inset path for the inner-border stroke — fully inside the fill area. */
  borderPath: string;
};

type BuiltBaselineSegment = {
  id: string;
  path: string;
  borderPath: string;
};

const cx = 50;
const cy = 50;

type DonutSliceShapeProps = {
  path: string;
  fill: string;
  /** Pre-computed inset path for the inner-border stroke — fully inside the fill area. */
  borderPath?: string;
};

/** Filled donut slice with a thin inner-aligned border drawn on an inset path. */
const DonutSliceShape: React.FC<DonutSliceShapeProps> = ({
  path,
  fill,
  borderPath,
}) => (
  <g>
    <path d={path} fill={fill} />
    {borderPath && (
      <path
        d={borderPath}
        fill="none"
        stroke={PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR}
        strokeWidth={PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB}
        strokeLinejoin="round"
      />
    )}
  </g>
);

export const ProductGroupsDonutChart: React.FC<
  ProductGroupsDonutChartProps
> = ({
  data,
  rotationOffsetDeg = 0,
  isDragging = false,
  slotRef,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}) => {
  const theme = useTheme();
  const { classes, cx: classNames } = useStyles({ isDragging });
  const fontFamily = theme.typography.fontFamily;
  const centerLabel = getProductGroupsDonutCenterLabel(data.center);

  const { baselineSegments, proposalSegments, showBaselineLayer } =
    useMemo(() => {
      const geometries = buildProductGroupDonutSliceGeometries(data.segments, {
        rotationOffsetDeg,
      });
      if (geometries.length === 0) {
        return {
          baselineSegments: [] as BuiltBaselineSegment[],
          proposalSegments: [] as BuiltSegment[],
          showBaselineLayer: false,
        };
      }

      const baselineSegments: BuiltBaselineSegment[] = [];
      const proposalSegments: BuiltSegment[] = [];

      const borderInset = PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_WIDTH_VB / 2;

      const buildBorderPath = (
        startAngle: number,
        endAngle: number,
        insetOuter: number,
        insetInner: number,
      ): string => {
        if (insetOuter <= insetInner) return '';
        const midRadius = (insetOuter + insetInner) / 2;
        const angularInsetDeg = (borderInset / midRadius) * (180 / Math.PI);
        const angularStart = startAngle + angularInsetDeg;
        const angularEnd = endAngle - angularInsetDeg;
        if (angularEnd <= angularStart) return '';
        return buildVariableOuterDonutPath(
          angularStart,
          angularEnd,
          insetOuter,
          {
            cx,
            cy,
            innerRadius: insetInner,
          },
        );
      };

      for (const geometry of geometries) {
        const baselinePath = buildVariableOuterDonutPath(
          geometry.startAngle,
          geometry.endAngle,
          geometry.baselineOuterRadiusVb,
          { cx, cy },
        );
        baselineSegments.push({
          id: geometry.id,
          path: baselinePath,
          borderPath: buildBorderPath(
            geometry.startAngle,
            geometry.endAngle,
            geometry.baselineOuterRadiusVb - borderInset,
            PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB + borderInset,
          ),
        });

        const proposalPath = buildVariableOuterDonutPath(
          geometry.startAngle,
          geometry.endAngle,
          geometry.proposalOuterRadiusVb,
          { cx, cy },
        );

        proposalSegments.push({
          ...geometry,
          path: proposalPath,
          borderPath: buildBorderPath(
            geometry.startAngle,
            geometry.endAngle,
            geometry.proposalOuterRadiusVb - borderInset,
            PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB + borderInset,
          ),
        });
      }

      return {
        baselineSegments,
        proposalSegments,
        showBaselineLayer: proposalSegments.some(
          (segment) => segment.cloudValue !== segment.value,
        ),
      };
    }, [data.segments, rotationOffsetDeg]);

  if (proposalSegments.length === 0) return null;

  return (
    <Box className={classes.root}>
      <Box
        ref={slotRef}
        className={classNames(classes.donutSlot, classes.donutSlotDraggable)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <svg
          className={classes.donutSvg}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="rotate(-90 50 50)">
            {showBaselineLayer &&
              baselineSegments.map((segment) => (
                <DonutSliceShape
                  key={`baseline-${segment.id}`}
                  path={segment.path}
                  fill={BASELINE_COLOR}
                  borderPath={segment.borderPath}
                />
              ))}
            {proposalSegments.map((segment) => (
              <Tooltip
                key={segment.id}
                title={formatProductGroupSegmentTooltip(segment)}
                placement="top"
                disableInteractive
              >
                <g className={classes.sliceHitTarget}>
                  <DonutSliceShape
                    path={segment.path}
                    fill={segment.color}
                    borderPath={segment.borderPath}
                  />
                </g>
              </Tooltip>
            ))}
          </g>
          {centerLabel !== undefined && (
            // Presentation attributes (not CSS) so html-to-image keeps the center label.
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={PRODUCT_GROUPS_TEXT_COLOR}
              fontFamily={fontFamily}
              fontSize={PRODUCT_GROUPS_CENTER_TEXT.reductionFontSizeVb}
              fontWeight={PRODUCT_GROUPS_CENTER_TEXT.reductionFontWeight}
              opacity={PRODUCT_GROUPS_CENTER_TEXT.reductionOpacity}
            >
              {centerLabel}
            </text>
          )}
        </svg>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{ isDragging: boolean }>()(
  (_theme, { isDragging }) => ({
    root: {
      position: 'absolute',
      inset: 0,
      overflow: 'visible',
      pointerEvents: 'none',
      zIndex: 0,
    },
    donutSlot: {
      position: 'absolute',
      top: `${PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT}%`,
      left: `${PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT}%`,
      width: `${PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT}%`,
      aspectRatio: '1',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'auto',
    },
    donutSlotDraggable: {
      touchAction: 'none',
      cursor: isDragging ? 'grabbing' : 'grab',
    },
    donutSvg: {
      display: 'block',
      width: '100%',
      height: '100%',
      overflow: 'visible',
    },
    sliceHitTarget: {
      cursor: 'inherit',
    },
  }),
);
